import { useState, useEffect } from "react";
import apiClient from "../services/apiClient";
import { detectBrandByDomain } from "../services/brandApi";

interface PublicCatalogEntry {
  product?: {
    trialDurationDays: number | null;
  };
}

interface PublicCatalogResponse {
  data: PublicCatalogEntry[];
  total: number;
}

export interface UseTrialDurationResult {
  days: number | null;
  isLoading: boolean;
}

/**
 * Fetches trial duration days from the public catalog API.
 *
 * AC-F1: Reads trial days from GET /billing/catalog?brandId=UUID.
 *        When brandId is null, auto-detects brand from current hostname.
 * AC-F2: Returns null on error — caller renders fallback text without days.
 *
 * @param brandId - Brand UUID (optional; auto-detects from hostname if null)
 * @returns { days, isLoading } — days is null until resolved or on error
 */
export function useTrialDuration(
  brandId?: string | null
): UseTrialDurationResult {
  const [days, setDays] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchTrialDuration = async () => {
      try {
        let resolvedBrandId = brandId;

        if (!resolvedBrandId) {
          const brand = await detectBrandByDomain(window.location.hostname);
          if (cancelled) return;
          resolvedBrandId = brand.id ?? null;
        }

        if (!resolvedBrandId) {
          if (!cancelled) setIsLoading(false);
          return;
        }

        const res = await apiClient.get<PublicCatalogResponse>(
          `/billing/catalog?brandId=${encodeURIComponent(resolvedBrandId)}`
        );
        if (cancelled) return;

        const entries = res.data?.data ?? [];
        for (const entry of entries) {
          const d = entry.product?.trialDurationDays;
          if (typeof d === "number" && d > 0) {
            setDays(d);
            break;
          }
        }
      } catch {
        // AC-F2: On error, keep null — caller renders fallback text
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchTrialDuration();

    return () => {
      cancelled = true;
    };
  }, [brandId]);

  return { days, isLoading };
}
